/**
 * Controlled vocabulary for a package's billing cadence.
 *
 * Mirrors `PACKAGE_CADENCES` in
 * `services/api/src/schemas/marketplace.schemas.ts` — keep both in sync so the
 * admin form never submits a value the API enum would reject. Ordered from
 * one-off to longest recurring interval for the admin dropdown.
 */
export const PACKAGE_CADENCES = [
  "one-time",
  "weekly",
  "monthly",
  "quarterly",
  "annually",
  "per-project",
] as const;

export type PackageCadence = (typeof PACKAGE_CADENCES)[number];

const CADENCE_LABEL_KEYS: Record<PackageCadence, string> = {
  "one-time": "marketplace.cadence.oneTime",
  weekly: "marketplace.cadence.weekly",
  monthly: "marketplace.cadence.monthly",
  quarterly: "marketplace.cadence.quarterly",
  annually: "marketplace.cadence.annually",
  "per-project": "marketplace.cadence.perProject",
};

export function isPackageCadence(
  value: string | null | undefined,
): value is PackageCadence {
  return value != null && (PACKAGE_CADENCES as readonly string[]).includes(value);
}

/**
 * Localized cadence label, falling back to the raw stored string for any legacy
 * value outside the controlled vocabulary so older data still renders.
 */
export function formatCadenceLabel(
  cadence: string | null | undefined,
  t: (key: string) => string,
): string | null {
  if (!cadence) return null;
  return isPackageCadence(cadence) ? t(CADENCE_LABEL_KEYS[cadence]) : cadence;
}

export const PACKAGE_CADENCE_OPTIONS = PACKAGE_CADENCES.map((value) => ({
  value,
  labelKey: CADENCE_LABEL_KEYS[value],
}));
